import { useEffect, useRef, useState } from 'react';
import type { Invoice, Payment } from '@bpd/shared';
import { computeInvoiceBalance, formatCurrency, type InvoiceStatus } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { CurrencyInput } from '../CurrencyInput.tsx';
import { PercentInput } from '../PercentInput.tsx';
import { loadTaskOptions, taskLabel, type TaskOption } from './taskOptions.ts';

const STATUS_LABEL: Record<InvoiceStatus, string> = { unpaid: 'Unpaid', partial: 'Partially paid', paid: 'Paid' };
const STATUS_BADGE_CLS: Record<InvoiceStatus, string> = {
  unpaid: 'bg-gray-100 text-gray-600',
  partial: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
};

/** What ProjectEstimatesSection's or ProjectProductsSection's "Convert to Invoice" button hands up
 * so this section can open its add-invoice form pre-filled — see ProjectDetailPage, which lifts
 * this between sections. Exactly one of sourceEstimateId/sourceProjectProductIds is set depending
 * on which section originated it. Not a live link: once created, the Invoice's own
 * billedAmount/title are independent of the source Estimate/Products (which can be edited or
 * deleted afterward without touching the invoice). */
export interface InvoicePrefill {
  title: string;
  billedAmount: number;
  taskItemId?: string;
  sourceEstimateId?: string;
  sourceProjectProductIds?: string[];
}

interface AddPaymentFormProps {
  invoiceId: string;
  /** The invoice's current balanceDue (computeInvoiceBalance) — "100%"/the dollar default both mean
   * paying this off in full, not the invoice's original billedAmount. */
  balanceDue: number;
  onAdded: (payment: Payment) => void;
  onCancel: () => void;
}

function AddPaymentForm({ invoiceId, balanceDue, onAdded, onCancel }: AddPaymentFormProps) {
  const [percent, setPercent] = useState('100');
  const [amount, setAmount] = useState(() => balanceDue.toFixed(2));
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  // "Schedule" = tee the payment up for whoever issues the check/transfer without it counting as
  // paid yet; they mark it paid later (see PaymentRow).
  const [scheduled, setScheduled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The two fields stay linked in both directions — editing one derives the other from the same
  // balanceDue, rather than picking a "mode" up front.
  function handlePercentChange(v: string) {
    setPercent(v);
    const p = Number(v);
    if (Number.isFinite(p)) setAmount(((balanceDue * p) / 100).toFixed(2));
  }

  function handleAmountChange(v: string) {
    setAmount(v);
    const a = Number(v);
    if (Number.isFinite(a) && balanceDue > 0) setPercent(String(Number(((a / balanceDue) * 100).toFixed(2))));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0 || !paidDate) {
      setError('A positive amount and a paid date are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createPayment({
        invoiceId,
        amount: amt,
        paidDate,
        status: scheduled ? 'scheduled' : 'paid',
        ...(note ? { note } : {}),
      });
      onAdded(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add payment');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="mt-2 space-y-2 rounded-md border border-gray-200 p-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-500">% of balance due</label>
          <PercentInput value={percent} onChange={handlePercentChange} className="mt-0.5" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">Amount</label>
          <CurrencyInput value={amount} onChange={handleAmountChange} min="0.01" className="mt-0.5" />
        </div>
      </div>
      <p className="text-xs text-gray-400">{formatCurrency(balanceDue)} left to pay or schedule</p>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} />
        Schedule only — not paid yet (someone will issue the check/transfer)
      </label>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          title={scheduled ? 'Planned payment date' : 'Paid date'}
          value={paidDate}
          onChange={(e) => setPaidDate(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <input
          type="text"
          placeholder="Note — e.g. Deposit"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : scheduled ? 'Schedule payment' : 'Add payment'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

interface PaymentRowProps {
  payment: Payment;
  invoiceId: string;
  onDeleted: (id: string) => void;
  onUpdated: (payment: Payment) => void;
}

function PaymentRow({ payment, invoiceId, onDeleted, onUpdated }: PaymentRowProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isScheduled = payment.status === 'scheduled';
  const [markingPaid, setMarkingPaid] = useState(false);
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flips a scheduled payment to paid with the ACTUAL date it went out — only now does it count
  // toward the invoice's paid total / balance due.
  async function handleMarkPaid() {
    if (!paidOn) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.updatePayment(payment.id, invoiceId, { status: 'paid', paidDate: paidOn });
      onUpdated(updated);
      setMarkingPaid(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mark paid');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    await projectsApi.deletePayment(payment.id, invoiceId);
    onDeleted(payment.id);
  }

  return (
    <div className="flex items-center gap-3 border-t border-gray-100 py-1.5 first:border-t-0">
      <span className="text-sm text-gray-600">{payment.paidDate}</span>
      <span className="text-sm font-medium text-gray-900">{formatCurrency(payment.amount)}</span>
      {isScheduled && (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          Scheduled — not paid
        </span>
      )}
      {payment.note && <span className="text-sm text-gray-500">{payment.note}</span>}
      <span className="flex-1" />
      {isScheduled && !markingPaid && (
        <button
          type="button"
          onClick={() => setMarkingPaid(true)}
          className="rounded-md bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700"
        >
          Mark paid
        </button>
      )}
      {isScheduled && markingPaid && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            title="Date the payment actually went out"
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-0.5 text-xs"
          />
          <button
            type="button"
            onClick={() => void handleMarkPaid()}
            disabled={saving}
            className="rounded-md bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Confirm paid'}
          </button>
          <button type="button" onClick={() => setMarkingPaid(false)} className="text-xs text-gray-500 hover:underline">
            Cancel
          </button>
        </div>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
      {!confirmDelete ? (
        <button type="button" onClick={() => setConfirmDelete(true)} className="text-xs text-red-600 hover:underline">
          Delete
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleDelete()}
            className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
          >
            Confirm
          </button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="text-xs text-gray-500 hover:underline">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // matches the estimate quote file limit
const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

interface InvoiceAttachmentProps {
  invoice: Invoice;
  onUpdated: (invoice: Invoice) => void;
}

/** Upload/view/replace/remove the vendor's invoice file (PDF/JPG/PNG). Same flow as the estimate
 * quote file: the browser PUTs straight to S3 via a presigned URL, then the key is persisted with
 * the ordinary updateInvoice call. One file per invoice — when a vendor bill is split across
 * several app invoices (one per project), attach the same file to each. */
function InvoiceAttachment({ invoice, onUpdated }: InvoiceAttachmentProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function handleFileChosen(file: File | null) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('File is too large (max 15MB)');
      return;
    }
    const contentType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : '');
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(contentType)) {
      setError('Only PDF, JPG, or PNG files are supported');
      return;
    }
    setBusy(true);
    try {
      const { uploadUrl, key } = await projectsApi.getInvoiceUploadUrl(invoice.id, invoice.projectId, contentType, file.name);
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('Upload to storage failed');
      const updated = await projectsApi.updateInvoice(invoice.id, invoice.projectId, {
        attachmentKey: key,
        attachmentFileName: file.name,
        attachmentContentType: contentType,
      });
      onUpdated(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload');
    } finally {
      setBusy(false);
    }
  }

  async function handleView() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await projectsApi.getInvoiceDownloadUrl(invoice.id, invoice.projectId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    setError(null);
    try {
      await projectsApi.deleteInvoiceAttachment(invoice.id, invoice.projectId);
      const { attachmentKey: _k, attachmentFileName: _f, attachmentContentType: _c, ...rest } = invoice;
      onUpdated(rest);
      setConfirmRemove(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-2 border-b border-gray-100 pb-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Invoice file</p>
      {invoice.attachmentFileName ? (
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-700">{invoice.attachmentFileName}</span>
          <button type="button" onClick={() => void handleView()} disabled={busy} className="text-sm text-phoenix-blue-600 hover:underline">
            View
          </button>
          <label className="cursor-pointer text-sm text-phoenix-blue-600 hover:underline">
            Replace
            <input
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              disabled={busy}
              onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
            />
          </label>
          {!confirmRemove ? (
            <button type="button" onClick={() => setConfirmRemove(true)} className="text-sm text-red-600 hover:underline">
              Remove
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={busy}
                className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
              >
                Confirm
              </button>
              <button type="button" onClick={() => setConfirmRemove(false)} className="text-sm text-gray-500 hover:underline">
                Cancel
              </button>
            </>
          )}
        </div>
      ) : (
        <label className="mt-1 inline-block cursor-pointer text-sm text-phoenix-blue-600 hover:underline">
          {busy ? 'Uploading…' : '+ Upload invoice (PDF, JPG, or PNG)'}
          <input
            type="file"
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            disabled={busy}
            onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

interface InvoiceFormFieldsProps {
  title: string;
  onTitle: (v: string) => void;
  billedAmount: string;
  onBilledAmount: (v: string) => void;
  taskItemId: string;
  onTaskItemId: (v: string) => void;
  dueDate: string;
  onDueDate: (v: string) => void;
  taskOptions: TaskOption[];
}

function InvoiceFormFields({
  title,
  onTitle,
  billedAmount,
  onBilledAmount,
  taskItemId,
  onTaskItemId,
  dueDate,
  onDueDate,
  taskOptions,
}: InvoiceFormFieldsProps) {
  return (
    <>
      <input
        type="text"
        placeholder="Invoice title *"
        value={title}
        onChange={(e) => onTitle(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        maxLength={200}
      />
      <div className="grid grid-cols-2 gap-2">
        <CurrencyInput value={billedAmount} onChange={onBilledAmount} placeholder="Billed amount *" />
        <input
          type="date"
          placeholder="Due date"
          value={dueDate}
          onChange={(e) => onDueDate(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500">Task / subtask (optional)</label>
        <select
          value={taskItemId}
          onChange={(e) => onTaskItemId(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          <option value="">— Project-level (no specific task) —</option>
          {taskOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

interface AddInvoiceFormProps {
  projectId: string;
  taskOptions: TaskOption[];
  prefill: InvoicePrefill | null;
  onAdded: (invoice: Invoice) => void;
  onCancel: () => void;
}

function AddInvoiceForm({ projectId, taskOptions, prefill, onAdded, onCancel }: AddInvoiceFormProps) {
  const [title, setTitle] = useState(prefill?.title ?? '');
  const [billedAmount, setBilledAmount] = useState(prefill ? String(prefill.billedAmount) : '');
  const [taskItemId, setTaskItemId] = useState(prefill?.taskItemId ?? '');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(billedAmount);
    if (!title.trim() || !Number.isFinite(amt) || amt < 0) {
      setError('Title and a non-negative billed amount are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createInvoice({
        projectId,
        title: title.trim(),
        billedAmount: amt,
        ...(taskItemId ? { taskItemId } : {}),
        ...(prefill?.sourceEstimateId ? { sourceEstimateId: prefill.sourceEstimateId } : {}),
        ...(prefill?.sourceProjectProductIds ? { sourceProjectProductIds: prefill.sourceProjectProductIds } : {}),
        ...(dueDate ? { dueDate } : {}),
      });
      onAdded(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    >
      {prefill && (
        <p className="rounded-md bg-phoenix-blue-50 px-2 py-1 text-xs text-phoenix-blue-700">
          {prefill.sourceProjectProductIds
            ? 'Pre-filled from the selected products — edit anything before saving.'
            : 'Pre-filled from the accepted estimate — edit anything before saving.'}
        </p>
      )}
      <InvoiceFormFields
        title={title}
        onTitle={setTitle}
        billedAmount={billedAmount}
        onBilledAmount={setBilledAmount}
        taskItemId={taskItemId}
        onTaskItemId={setTaskItemId}
        dueDate={dueDate}
        onDueDate={setDueDate}
        taskOptions={taskOptions}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create invoice'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

interface EditInvoiceFormProps {
  invoice: Invoice;
  taskOptions: TaskOption[];
  onSaved: (invoice: Invoice) => void;
  onCancel: () => void;
}

function EditInvoiceForm({ invoice, taskOptions, onSaved, onCancel }: EditInvoiceFormProps) {
  const [title, setTitle] = useState(invoice.title);
  const [billedAmount, setBilledAmount] = useState(String(invoice.billedAmount));
  const [taskItemId, setTaskItemId] = useState(invoice.taskItemId ?? '');
  const [dueDate, setDueDate] = useState(invoice.dueDate ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const amt = Number(billedAmount);
    if (!title.trim() || !Number.isFinite(amt) || amt < 0) {
      setError('Title and a non-negative billed amount are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.updateInvoice(invoice.id, invoice.projectId, {
        title: title.trim(),
        billedAmount: amt,
        taskItemId: taskItemId || null,
        dueDate: dueDate || null,
      });
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-phoenix-blue-200 bg-phoenix-blue-50/40 p-3">
      <InvoiceFormFields
        title={title}
        onTitle={setTitle}
        billedAmount={billedAmount}
        onBilledAmount={setBilledAmount}
        taskItemId={taskItemId}
        onTaskItemId={setTaskItemId}
        dueDate={dueDate}
        onDueDate={setDueDate}
        taskOptions={taskOptions}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

/** What an Estimate needs to know about every Invoice/Payment in the project, to draw down its own
 * "still estimated" contribution — see computeEstimateRemaining in @bpd/shared, which matches by
 * taskItemId (task-linked estimates) or sourceEstimateId (task-less ones). Reported for every
 * invoice, not just ones explicitly converted from an estimate. */
export interface InvoiceActivity {
  sourceEstimateId?: string;
  /** Every Products-line id this invoice was converted from — lets ProjectProductsSection mark
   * those lines "Converted to invoice" and avoid offering to convert them again. */
  sourceProjectProductIds?: string[];
  taskItemId?: string;
  billedAmount: number;
  paidTotal: number;
}

interface ProjectInvoicesSectionProps {
  projectId: string;
  /** Set by ProjectDetailPage right after "Convert to Invoice" is clicked on an estimate — opens
   * the add-invoice form pre-filled. Cleared via onPrefillConsumed once the form has picked it up,
   * so re-opening "+ New invoice" manually afterward starts blank again. */
  prefill: InvoicePrefill | null;
  onPrefillConsumed: () => void;
  /** Reports Invoiced/Paid totals up to the parent for the page-level Budget summary — recomputed
   * whenever invoices/payments change. Mirrors ProjectEstimatesSection's onAcceptedTotalChange. */
  onTotalsChange: (totals: { invoicedTotal: number; paidTotal: number }) => void;
  /** Reports per-invoice paid totals (keyed by sourceEstimateId where set) up to ProjectDetailPage,
   * which forwards it into ProjectEstimatesSection so a converted estimate can draw itself down and
   * show as "Converted" instead of offering to convert again. */
  onInvoiceActivityChange: (activity: InvoiceActivity[]) => void;
}

export function ProjectInvoicesSection({
  projectId,
  prefill,
  onPrefillConsumed,
  onTotalsChange,
  onInvoiceActivityChange,
}: ProjectInvoicesSectionProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentsByInvoice, setPaymentsByInvoice] = useState<Record<string, Payment[]>>({});
  const [taskOptions, setTaskOptions] = useState<TaskOption[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [addingPaymentFor, setAddingPaymentFor] = useState<string | null>(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [confirmDeleteInvoiceId, setConfirmDeleteInvoiceId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  // Brief highlight right after a "Convert to Invoice" prefill lands here, so the scroll-jump
  // (below) reads as "your click did something" rather than looking like nothing happened.
  const [justPrefilled, setJustPrefilled] = useState(false);

  useEffect(() => {
    void Promise.all([projectsApi.listInvoices(projectId), loadTaskOptions(projectId)]).then(
      async ([invoiceData, taskOptionData]) => {
        setInvoices(invoiceData);
        setTaskOptions(taskOptionData);
        // Every invoice's payments are needed up front for the page-level Paid total and the
        // Estimate drawdown (below) — NOT just the ones the admin happens to expand, otherwise
        // both would silently read as $0 for any invoice never clicked open.
        const allPayments = await Promise.all(invoiceData.map((inv) => projectsApi.listPayments(inv.id)));
        setPaymentsByInvoice((prev) => {
          const next = { ...prev };
          invoiceData.forEach((inv, idx) => {
            next[inv.id] = allPayments[idx]!;
          });
          return next;
        });
        setLoaded(true);
      },
    );
  }, [projectId]);

  useEffect(() => {
    if (!prefill) return;
    setShowAddInvoice(true);
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setJustPrefilled(true);
    const timer = setTimeout(() => setJustPrefilled(false), 1500);
    return () => clearTimeout(timer);
  }, [prefill]);

  useEffect(() => {
    const invoicedTotal = invoices.reduce((sum, inv) => sum + inv.billedAmount, 0);
    const activity: InvoiceActivity[] = invoices.map((inv) => ({
      ...(inv.sourceEstimateId ? { sourceEstimateId: inv.sourceEstimateId } : {}),
      ...(inv.sourceProjectProductIds ? { sourceProjectProductIds: inv.sourceProjectProductIds } : {}),
      ...(inv.taskItemId ? { taskItemId: inv.taskItemId } : {}),
      billedAmount: inv.billedAmount,
      paidTotal: computeInvoiceBalance(inv, paymentsByInvoice[inv.id] ?? []).paidTotal,
    }));
    const paidTotal = activity.reduce((sum, a) => sum + a.paidTotal, 0);
    onTotalsChange({ invoicedTotal, paidTotal });
    onInvoiceActivityChange(activity);
  }, [invoices, paymentsByInvoice, onTotalsChange, onInvoiceActivityChange]);

  async function toggleExpand(invoiceId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
    if (!(invoiceId in paymentsByInvoice)) {
      const payments = await projectsApi.listPayments(invoiceId);
      setPaymentsByInvoice((prev) => ({ ...prev, [invoiceId]: payments }));
    }
  }

  async function handleDeleteInvoice(id: string) {
    await projectsApi.deleteInvoice(id, projectId);
    setInvoices((prev) => prev.filter((i) => i.id !== id));
    setPaymentsByInvoice((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setConfirmDeleteInvoiceId(null);
  }

  function handleInvoiceUpdated(updated: Invoice) {
    setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    setEditingInvoiceId(null);
  }

  if (!loaded) return null;

  return (
    <section
      ref={sectionRef}
      className={`mt-10 scroll-mt-4 rounded-lg transition-shadow duration-500 ${
        justPrefilled ? 'ring-2 ring-phoenix-blue-400' : 'ring-2 ring-transparent'
      }`}
    >
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-phoenix-blue-900">Invoices</h2>
        <button
          type="button"
          onClick={() => {
            if (showAddInvoice) onPrefillConsumed();
            setShowAddInvoice((v) => !v);
          }}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
        >
          {showAddInvoice ? 'Cancel' : '+ New invoice'}
        </button>
      </div>

      {showAddInvoice && (
        <AddInvoiceForm
          projectId={projectId}
          taskOptions={taskOptions}
          prefill={prefill}
          onAdded={(invoice) => {
            setInvoices((prev) => [...prev, invoice]);
            setPaymentsByInvoice((prev) => ({ ...prev, [invoice.id]: [] }));
            setShowAddInvoice(false);
            onPrefillConsumed();
          }}
          onCancel={() => {
            setShowAddInvoice(false);
            onPrefillConsumed();
          }}
        />
      )}

      {invoices.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">No invoices yet.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {invoices.map((invoice) => {
            const payments = paymentsByInvoice[invoice.id] ?? [];
            const { paidTotal, scheduledTotal, balanceDue, status } = computeInvoiceBalance(invoice, payments);
            const isExpanded = expanded.has(invoice.id);
            const isEditing = editingInvoiceId === invoice.id;
            return (
              <div key={invoice.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                {isEditing ? (
                  <EditInvoiceForm
                    invoice={invoice}
                    taskOptions={taskOptions}
                    onSaved={handleInvoiceUpdated}
                    onCancel={() => setEditingInvoiceId(null)}
                  />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => void toggleExpand(invoice.id)}
                      className="flex flex-wrap items-center gap-2 text-left"
                    >
                      <span className="text-xs text-phoenix-blue-500">{isExpanded ? '▼' : '▶'}</span>
                      <span className="font-semibold text-gray-900">{invoice.title}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLS[status]}`}>
                        {STATUS_LABEL[status]}
                      </span>
                      {invoice.taskItemId && (
                        <span className="text-xs text-gray-500">— {taskLabel(taskOptions, invoice.taskItemId)}</span>
                      )}
                      {invoice.attachmentFileName && (
                        <span className="text-xs text-gray-500" title={invoice.attachmentFileName}>
                          📎
                        </span>
                      )}
                      {invoice.sourceEstimateId && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                          From estimate
                        </span>
                      )}
                      {invoice.sourceProjectProductIds && invoice.sourceProjectProductIds.length > 0 && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                          From products
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>
                        {formatCurrency(paidTotal)} paid of {formatCurrency(invoice.billedAmount)}
                        {balanceDue > 0 && <> — {formatCurrency(balanceDue)} due</>}
                        {scheduledTotal > 0 && (
                          <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            {formatCurrency(scheduledTotal)} scheduled
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingInvoiceId(invoice.id)}
                        className="text-gray-500 hover:underline"
                      >
                        Edit
                      </button>
                      {confirmDeleteInvoiceId !== invoice.id ? (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteInvoiceId(invoice.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => void handleDeleteInvoice(invoice.id)}
                            className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteInvoiceId(null)}
                            className="text-gray-500 hover:underline"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {isExpanded && !isEditing && (
                  <div className="mt-3 border-t border-gray-100 pt-2">
                    <InvoiceAttachment
                      invoice={invoice}
                      onUpdated={(updated) =>
                        setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
                      }
                    />
                    {invoice.dueDate && <p className="text-xs text-gray-500">Due {invoice.dueDate}</p>}
                    {payments.length === 0 && <p className="py-1 text-sm text-gray-400">No payments recorded yet.</p>}
                    {payments.map((payment) => (
                      <PaymentRow
                        key={payment.id}
                        payment={payment}
                        invoiceId={invoice.id}
                        onDeleted={(id) =>
                          setPaymentsByInvoice((prev) => ({
                            ...prev,
                            [invoice.id]: (prev[invoice.id] ?? []).filter((p) => p.id !== id),
                          }))
                        }
                        onUpdated={(updated) =>
                          setPaymentsByInvoice((prev) => ({
                            ...prev,
                            [invoice.id]: (prev[invoice.id] ?? []).map((p) => (p.id === updated.id ? updated : p)),
                          }))
                        }
                      />
                    ))}

                    {status === 'paid' ? (
                      <p className="mt-2 text-sm font-medium text-green-700">
                        Paid in full — no balance remaining.
                      </p>
                    ) : addingPaymentFor === invoice.id ? (
                      <AddPaymentForm
                        invoiceId={invoice.id}
                        balanceDue={Math.max(0, balanceDue - scheduledTotal)}
                        onAdded={(payment) => {
                          setPaymentsByInvoice((prev) => ({
                            ...prev,
                            [invoice.id]: [...(prev[invoice.id] ?? []), payment],
                          }));
                          setAddingPaymentFor(null);
                        }}
                        onCancel={() => setAddingPaymentFor(null)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingPaymentFor(invoice.id)}
                        className="mt-2 text-sm text-phoenix-blue-600 hover:underline"
                      >
                        + Record or schedule payment
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
